/**
 * Resolves once every registered `prism-*` element under `root` has hydrated (Stencil adds the
 * `hydrated` class; until then the element is `visibility: hidden`) and painted, or after
 * `timeoutMs`.
 *
 * Used to hold a faded-out view until its Prism controls can paint, so the view appears whole
 * instead of as a heading and buttons with empty space where the cards land a frame or two
 * later. Elements whose tag is not registered are ignored, so this never waits on markup that
 * cannot hydrate (and resolves immediately in jsdom).
 */
export default function waitForPrismHydration(root, timeoutMs = 400) {
  return new Promise((resolve) => {
    if (!root || typeof MutationObserver === 'undefined' || typeof customElements === 'undefined') {
      resolve();
      return;
    }

    const registered = () =>
      Array.from(root.querySelectorAll('*')).filter(
        (el) => el.localName.startsWith('prism-') && customElements.get(el.localName)
      );
    const pending = () => registered().some((el) => !el.classList.contains('hydrated'));

    if (registered().length === 0) {
      resolve();
      return;
    }

    // Hydration flips the host to visible, but Prism's internals inherit that through their
    // own `transition: all` (e.g. `prism-card`'s inner section), so they stay hidden for the
    // first frame of that transition. Two frames lets it start before anything is revealed.
    const settle = () => {
      if (typeof requestAnimationFrame !== 'function') {
        resolve();
        return;
      }
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    };

    if (!pending()) {
      settle();
      return;
    }

    let timer = null;
    let observer = null;
    const finish = (hydrated) => {
      observer.disconnect();
      clearTimeout(timer);
      if (hydrated) settle();
      else resolve();
    };
    observer = new MutationObserver(() => {
      if (!pending()) finish(true);
    });
    observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    timer = setTimeout(() => finish(false), timeoutMs);
  });
}